/**
 * One check-in, in full, for the front desk. Rendered inside a Dialog by
 * VisitStartCheckInsPage — this component owns no page chrome and does its own
 * fetching of nothing; the session is already in memory (VisitStartCheckInsPage
 * reads it from the same list subscription that drew the inbox row).
 *
 * The clinical summary is VisitStartIntakeSummary — the same component a clinician
 * sees after redeeming the patient's 7-digit code. Deliberately the same: the two
 * screens read the same intake, and a "no" that appears in one and not the other
 * is a clinical disagreement produced by a copy-paste. It is given no `record` or
 * `allowedSections` here, because a facility cannot read a patient's chart, so
 * nothing is suppressed as a duplicate and every line the patient gave is printed.
 *
 * Below that, only the answers the summary does NOT already print — it covers
 * identity, the chief complaint, medications, allergies and the referral letter in
 * full, so listing every element again underneath would print the same lines
 * twice on one screen.
 */

import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { contentCardSx } from "styles/standaloneLayoutTokens";
import VisitStartIntakeSummary from "components/dashboard/healthRecordsShare/VisitStartIntakeSummary";
import {
  checkInClarifications,
  checkInElements,
} from "components/dashboard/visitStartCheckIns/visitStartCheckInSummary";
import {
  answerFieldLabel,
  answerValueLabel,
} from "components/dashboard/visitStartCheckIns/visitStartAnswerFormat";

/**
 * Elements VisitStartIntakeSummary renders above in full. Listing them again
 * below is the same answers twice.
 */
const SUMMARISED_ELEMENTS = new Set([
  "identity",
  "chief-complaint",
  "medication-list",
  "allergies",
  "referral-verification",
]);

export default function VisitStartCheckInDetail({ session }) {
  const intake = {
    sessionId: session.id,
    locationId: session.locationId || null,
    status: session.status || null,
    guest: Boolean(session.guest),
    cmrShared: Boolean(session.cmrShared),
    enabledElements: session.enabledElements || [],
    elementAnswers: session.elementAnswers || {},
    clarifyingQuestions: session.clarifyingQuestions || [],
  };

  const clarifications = checkInClarifications(session);
  const elements = checkInElements(session).filter(
    (element) => !SUMMARISED_ELEMENTS.has(element.elementId),
  );

  return (
    <>
      <Box sx={{ mb: 3 }}>
        <VisitStartIntakeSummary intake={intake} />
      </Box>

      {clarifications.length ? (
        <Paper variant="outlined" sx={{ ...contentCardSx, p: { xs: 2, sm: 3 }, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            Worth asking at the desk
          </Typography>
          {/* The whole reason these are recorded apart from the answers: "I'm not
              sure" is not a clinical no, and a human resolving it in person is
              what the field is for. */}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The patient was unsure about these, or preferred not to answer them on
            a screen.
          </Typography>
          <Stack spacing={1.5}>
            {clarifications.map((entry, index) => (
              <Box key={`${entry.elementId}-${index}`}>
                <Typography variant="body2">{entry.question}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {entry.elementLabel}
                  {entry.reason === "prefer_not_to_answer"
                    ? " · preferred not to answer"
                    : " · not sure"}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Paper>
      ) : null}

      {elements.length ? (
        <Paper variant="outlined" sx={{ ...contentCardSx, p: { xs: 2, sm: 3 } }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Everything else they answered
          </Typography>
          <Stack divider={<Divider />} spacing={2}>
            {elements.map((element) => (
              <Box key={element.elementId}>
                <Typography variant="subtitle2">{element.label}</Typography>
                {element.answered ? (
                  <AnswerBlock answer={element.answer} />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Not answered
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>
        </Paper>
      ) : null}
    </>
  );
}

VisitStartCheckInDetail.propTypes = {
  session: PropTypes.object.isRequired,
};

/**
 * One element's answer, rendered from its shape rather than from a per-element
 * mapping.
 *
 * A mapping keyed by elementId would be a second place to update every time an
 * element is added, and the failure mode is silent: a new element's answers would
 * simply not appear. The shapes are few (see SPEC.md §4) and each carries its own
 * question text where it has one, so this reads them structurally and falls back
 * to labelled key/value lines.
 */
function AnswerBlock({ answer }) {
  if (answer === null || answer === undefined) return null;

  // Screening screens: { answers: [{ id, question, value, label }] }. A table
  // scans faster than a stacked list once a screen asks eight-plus questions.
  //
  // table-layout: fixed plus explicit widths on every cell (not just the
  // header) — a plain auto-layout table sizes its columns from its own
  // longest cell, so two of these side by side (one screen's questions run
  // longer than another's) land on two different "Answer" widths. Fixed
  // widths make every table on the page agree regardless of its own content.
  if (Array.isArray(answer.answers)) {
    return (
      <TableContainer sx={{ mt: 0.5 }}>
        <Table size="small" sx={{ tableLayout: "fixed" }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, width: "70%" }}>
                Question
              </TableCell>
              <TableCell sx={{ fontWeight: 600, width: "30%" }}>
                Answer
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {answer.answers.map((row, index) => (
              <TableRow key={row?.id || index}>
                <TableCell sx={{ width: "70%" }}>{row?.question}</TableCell>
                <TableCell sx={{ width: "30%", fontWeight: 600 }}>
                  {row?.label || row?.value}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  // The interview: show what the patient confirmed, not the whole transcript.
  // The transcript is on the document if anyone needs it, but it is a
  // conversation, and the summary is the part they signed off on.
  if (answer.confirmedSummary || answer.summary) {
    return (
      <Typography variant="body2" sx={{ mt: 0.5 }}>
        {answer.confirmedSummary ||
          answer.summary?.narrative ||
          answer.summary?.chiefComplaint}
      </Typography>
    );
  }

  if (Array.isArray(answer.medications)) {
    if (!answer.medications.length) {
      return (
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          {answer.takingMedications === false
            ? "No regular medications."
            : "None listed."}
        </Typography>
      );
    }
    return (
      <Stack spacing={0.5} sx={{ mt: 0.5 }}>
        {answer.medications.map((med, index) => (
          <Typography variant="body2" key={`${med?.name || "med"}-${index}`}>
            {[med?.name, med?.dosage, med?.frequency].filter(Boolean).join(", ")}
          </Typography>
        ))}
      </Stack>
    );
  }

  if (Array.isArray(answer.allergies)) {
    if (!answer.allergies.length) {
      return (
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          None listed.
        </Typography>
      );
    }
    return (
      <Stack spacing={0.5} sx={{ mt: 0.5 }}>
        {answer.allergies.map((item, index) => (
          <Typography variant="body2" key={`${item?.allergen || "allergy"}-${index}`}>
            {[item?.allergen, item?.reaction].filter(Boolean).join(" — ")}
          </Typography>
        ))}
      </Stack>
    );
  }

  // Everything else: the small flat shapes (dependents, ncd-follow-up, the
  // single-bubble elements). Both halves go through visitStartAnswerFormat, so a
  // stored `hasReferral: false` reads "Has referral: No" rather than printing a
  // field name and a boolean at a receptionist.
  const entries = Object.entries(answer).filter(
    ([, value]) =>
      value !== null &&
      value !== undefined &&
      value !== "" &&
      typeof value !== "object",
  );
  if (!entries.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        Answered.
      </Typography>
    );
  }
  return (
    <Stack spacing={0.5} sx={{ mt: 0.5 }}>
      {entries.map(([key, value]) => (
        <Typography variant="body2" key={key}>
          {answerFieldLabel(key)}:{" "}
          <Box component="span" sx={{ fontWeight: 600 }}>
            {answerValueLabel(value)}
          </Box>
        </Typography>
      ))}
    </Stack>
  );
}

AnswerBlock.propTypes = {
  answer: PropTypes.any,
};
